import { Switch } from '@mantine/core'
import { getHumanDateFromTimestamp } from '@pubstack/common/src/date'
import { adUnitsDownloadFileId, allMappingFileId, biddersMappingDownloadFileId, MappingDownloadType, MappingExport } from '@pubstack/common/src/mappingExport'
import { AdminSite } from '@pubstack/common/src/site'
import { FunctionComponent, useCallback, useState } from 'react'
import { useUpdateAdminSite } from '~/api/admin/admin-site.api'
import { useCreateMappingExport } from '~/api/admin/mapping-export.api'
import Button from '~/components/Button'
import { FileDownload } from '~/components/FileDownload'
import { Flyout } from '~/components/Flyout'
import { Spinner } from '~/components/Spinner'
import { Toggle } from '~/components/Toggle'
import { Tooltip } from '~/components/Tooltip'

type Props = {
  isInitialLoading: boolean
  isDownloading: Record<MappingDownloadType, boolean>
  site?: AdminSite
  mappingExportLoading: boolean
  mappingExport?: MappingExport
  /** Current value of the server-side test bidder; `undefined` while it is still loading. */
  testBidderEnabled?: boolean
  /** The whole scope is enabled: the toggle then shows on and is locked, the site cannot opt out individually. */
  testBidderEnabledByScope?: boolean
  /** Whether the site/scope can use the test bidder; the toggle is hidden unless it is available or still enabled (then it can only be turned off). */
  isTestBidderAvailable?: boolean
  onTestBidderChange?: (enabled: boolean) => void | Promise<unknown>
  onRefreshClick: () => void
  onDownloadClick: (type: MappingDownloadType, fileName: string) => void
}

const PureAdminScopeSiteSetupFlyout: FunctionComponent<Props> = ({
  isInitialLoading,
  isDownloading,
  site,
  mappingExportLoading,
  mappingExport,
  testBidderEnabled,
  testBidderEnabledByScope = false,
  isTestBidderAvailable = false,
  onTestBidderChange,
  onRefreshClick,
  onDownloadClick,
}) => {
  // Context
  const scopeId = site?.scopeId ?? ''
  const siteId = site?.id ?? ''

  // State
  const [localSite, setLocalSite] = useState<AdminSite | undefined>(site)
  // Resync the editable copy when the site prop changes ("previous value" pattern).
  const [prevSite, setPrevSite] = useState(site)
  if (site !== prevSite) {
    setPrevSite(site)
    setLocalSite(site)
  }

  // Mutations
  const { mutateAsync: editSite } = useUpdateAdminSite(scopeId, siteId, true, {
    successToast: (
      <>
        <strong>{localSite?.name}</strong> was successfully updated,
      </>
    ),
    errorToast: 'Something went wrong when trying to update this site',
  })
  const { mutateAsync: createMappingExport, isPending: isCreatingMappingExport } = useCreateMappingExport(scopeId, siteId)

  const filenamePlaceholder = mappingExport?.status === 'INPROGRESS' ? 'creating file...' : mappingExport?.status === 'FAILED' ? 'error while creating file, retry generation' : 'no file'

  const toggleMappingRetrievalChange = useCallback(async () => {
    if (!localSite) return

    const admOnboarding = !localSite.admOnboarding
    const updatedSite: AdminSite = { ...localSite, admOnboarding }

    setLocalSite(updatedSite)

    await editSite(updatedSite)
  }, [localSite, editSite])

  const [isUpdatingTestBidder, setIsUpdatingTestBidder] = useState(false)
  const toggleTestBidderChange = useCallback(async () => {
    if (testBidderEnabled === undefined || testBidderEnabledByScope) return
    setIsUpdatingTestBidder(true)
    try {
      await onTestBidderChange?.(!testBidderEnabled)
    } catch {
      // The mutation hook already raised the error toast.
    } finally {
      setIsUpdatingTestBidder(false)
    }
  }, [testBidderEnabled, testBidderEnabledByScope, onTestBidderChange])
  // A stale `enabled` flag must stay reachable once the site stops being eligible, or it could never be cleaned up.
  const showTestBidder = isTestBidderAvailable || testBidderEnabled === true || testBidderEnabledByScope

  const handleFilesGenerationClick = useCallback(async () => {
    if (!scopeId || !siteId) return

    await createMappingExport({ scopeId, tagId: siteId })
  }, [scopeId, siteId, createMappingExport])

  return (
    <Flyout.Content>
      {!localSite || isInitialLoading ? (
        <Spinner />
      ) : (
        <>
          <Flyout.Title>{localSite.name}</Flyout.Title>

          <Flyout.Body>
            <h2 className="mb-4 font-rubik text-base leading-[26px] font-medium">Gather inventory information such as ad units and mapping</h2>

            <Tooltip title="Activate mapping retrieval from the collector">
              <Toggle value={localSite.admOnboarding} onClick={toggleMappingRetrievalChange} trackingLabel={`admin-adm-onboarding-${localSite.admOnboarding ? 'unset' : 'set'}`} />
              <span className="ml-2">Mapping retrieval</span>
            </Tooltip>
            <div className="ml-[52px] font-roboto text-sm leading-[22px] text-slate-grey">
              {localSite.admOnboardingEnabledTime ? `Started on: ${getHumanDateFromTimestamp(localSite.admOnboardingEnabledTime, 'dd LLL yyyy, hh:mm a')}` : 'Not started'}
            </div>

            {showTestBidder && (
              <div className="mt-6">
                <Switch
                  label="Server-side test bidder"
                  checked={testBidderEnabledByScope || testBidderEnabled === true}
                  disabled={testBidderEnabled === undefined || isUpdatingTestBidder || testBidderEnabledByScope}
                  onChange={toggleTestBidderChange}
                />
                <div className="ml-[52px] font-roboto text-sm leading-[22px] text-slate-grey">
                  Answers server-side auctions of this site with a test bid, for the IPs allowed on the scope page (Features). Never sent to analytics.
                </div>
                {testBidderEnabledByScope && <div className="ml-[52px] font-roboto text-sm leading-[22px] text-slate-grey">Enabled for all sites of this scope (scope Features page)</div>}
                {!isTestBidderAvailable && !testBidderEnabledByScope && (
                  <div className="ml-[52px] font-roboto text-sm leading-[22px] text-slate-grey">Not available on this site anymore — turn off to clean up.</div>
                )}
              </div>
            )}

            <h2 className="mt-6 mb-4 font-rubik text-base leading-[26px] font-medium">Generate configuration files</h2>
            <div className="mb-4 flex justify-between">
              <Button disabled={mappingExportLoading || isCreatingMappingExport} variant="secondary" onClick={handleFilesGenerationClick} trackingLabel="admin-adm-onboarding-files-generation-new">
                Generate files
              </Button>
              <Button
                disabled={mappingExportLoading || isCreatingMappingExport || !mappingExport?.id}
                variant={'tertiary'}
                iconName={'refresh'}
                onClick={onRefreshClick}
                trackingLabel={'admin-adm-onboarding-files-generation-refresh'}
              >
                Refresh
              </Button>
            </div>

            <div className="text-center">
              {mappingExportLoading || isCreatingMappingExport ? (
                <Spinner />
              ) : (
                <>
                  <div className="mr-1 *:w-full *:border-t *:border-platinum *:py-4 last-of-type:*:border-b">
                    <FileDownload
                      label="Ad units list"
                      filenamePlaceholder={filenamePlaceholder}
                      filename={mappingExport?.status === 'CREATED' ? `${adUnitsDownloadFileId(localSite.name)}.csv` : undefined}
                      trackingLabel="admin-adm-onboarding-ad-units-download"
                      isDownloading={isDownloading['adunits']}
                      onDownload={() => mappingExport?.id && onDownloadClick('adunits', adUnitsDownloadFileId(localSite.name))}
                    />
                  </div>
                  <div className="mr-1 *:w-full *:border-t *:border-platinum *:py-4 last-of-type:*:border-b">
                    <FileDownload
                      label="Mapping"
                      filenamePlaceholder={filenamePlaceholder}
                      filename={mappingExport?.status === 'CREATED' ? `${biddersMappingDownloadFileId(localSite.name)}.zip` : undefined}
                      trackingLabel="admin-adm-onboarding-mapping-download"
                      isDownloading={isDownloading['bidders-mapping']}
                      onDownload={() => mappingExport?.id && onDownloadClick('bidders-mapping', biddersMappingDownloadFileId(localSite.name))}
                    />
                  </div>

                  {mappingExport?.status === 'CREATED' && (
                    <div className="mt-4 flex justify-end">
                      <Button
                        variant="primary"
                        disabled={isDownloading['all']}
                        iconSpin={isDownloading['all']}
                        iconName={isDownloading['all'] ? 'pubstack' : 'download'}
                        trackingLabel="admin-adm-onboarding-mapping-all-download"
                        onClick={() => mappingExport.id && onDownloadClick('all', allMappingFileId(localSite.name))}
                      >
                        Download files
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </Flyout.Body>
        </>
      )}
    </Flyout.Content>
  )
}

export default PureAdminScopeSiteSetupFlyout
